import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { hasLiveApi } from '@/api/client'
import {
  getBettickerMapping,
  updateBettickerMapping,
  type BettickerMapping,
} from './bettickerMapping'

const QUERY_KEY = ['book', 'betticker-mapping'] as const

/** Lab ColorItems — doubtful yellow / watchlist red (no Smart Members on Agent). */
export const TICKER_FLAG_COLORS = {
  doubtfull: '#f4dc11',
  watchlist: '#ee1620',
} as const

export type TickerMemberFlag = keyof typeof TICKER_FLAG_COLORS

export function useBettickerMapping(enabled = true) {
  return useQuery({
    queryKey: QUERY_KEY,
    enabled: enabled && hasLiveApi(),
    staleTime: 30_000,
    queryFn: () => getBettickerMapping(),
  })
}

export function useUpdateBettickerMapping() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: {
      mappedDoubtfullUsers?: string[]
      mappedDoubtfullIps?: string[]
      mappedWatchlistUsers?: string[]
      mappedWatchlistIps?: string[]
    }) => updateBettickerMapping(body),
    onSuccess: async (res) => {
      qc.setQueryData(QUERY_KEY, res)
      await qc.invalidateQueries({ queryKey: QUERY_KEY })
    },
  })
}

function inList(value: string | undefined | null, list: string[] | undefined): boolean {
  const key = (value || '').trim().toLowerCase()
  if (!key || !list?.length) return false
  return list.some((u) => u.trim().toLowerCase() === key)
}

export function isDoubtfullMember(
  username: string | undefined | null,
  clientIp: string | undefined | null,
  mapping: Pick<BettickerMapping, 'mappedDoubtfullUsers' | 'mappedDoubtfullIps'> | undefined,
): boolean {
  if (!mapping) return false
  return (
    inList(username, mapping.mappedDoubtfullUsers) || inList(clientIp, mapping.mappedDoubtfullIps)
  )
}

export function isWatchlistMember(
  username: string | undefined | null,
  clientIp: string | undefined | null,
  mapping: Pick<BettickerMapping, 'mappedWatchlistUsers' | 'mappedWatchlistIps'> | undefined,
): boolean {
  if (!mapping) return false
  return (
    inList(username, mapping.mappedWatchlistUsers) || inList(clientIp, mapping.mappedWatchlistIps)
  )
}

/**
 * Lab getAllBets member color — prefer bet.doubtfull / bet.watchlist stamps,
 * then mapping lists (Smart Members skipped on Agent).
 */
export function resolveTickerMemberFlag(
  username: string | undefined | null,
  clientIp: string | undefined | null,
  mapping: BettickerMapping | undefined,
  stamps?: { doubtfull?: boolean | null; watchlist?: boolean | null },
): TickerMemberFlag | null {
  if (stamps?.doubtfull) return 'doubtfull'
  if (stamps?.watchlist) return 'watchlist'
  if (!mapping) return null
  if (isDoubtfullMember(username, clientIp, mapping)) return 'doubtfull'
  if (isWatchlistMember(username, clientIp, mapping)) return 'watchlist'
  return null
}
